export default {
  title: 'Profile',
  headerSubtitle: 'Manage your personal data and settings',

  tabs: {
    personal: 'Personal Information',
    nutrition: 'Nutrition Preferences',
    security: 'Security & Privacy',
    subscription: 'Subscription & Payments',
    referral: 'Referral Program',
  },

  personal: {
    title: 'Personal Information',
    subtitle: 'Manage your basic profile data',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    gender: 'Gender',
    genderOptions: {
      male: 'Male',
      female: 'Female',
      diverse: 'Diverse',
    },
    age: 'Age',
    ageUnit: 'years',
    language: 'Language',
    languageOptions: {
      de: 'Deutsch',
      en: 'English',
    },
  },

  body: {
    title: 'Body Data',
    subtitle: 'Your current body measurements',
    height: 'Height',
    heightUnit: 'cm',
    currentWeight: 'Current Weight',
    weightUnit: 'kg',
    targetWeight: 'Target Weight',
    bmi: 'BMI (Body Mass Index)',
    bmiCategories: {
      underweight: 'Underweight',
      normal: 'Normal Weight',
      overweight: 'Overweight',
      obese: 'Obese',
    },
    remaining: 'Remaining',
  },

  goals: {
    title: 'Goals & Activity',
    subtitle: 'Define your fitness and nutrition goals',
    mainGoal: 'Main Goal',
    goalOptions: {
      lose: 'Lose Weight',
      build: 'Build Muscle',
      maintain: 'Maintain Weight',
    },
    activityLevel: 'Activity Level',
    activityOptions: {
      sedentary: 'Sedentary (little movement)',
      lightly: 'Lightly active (1-3 days/week)',
      moderately: 'Moderately active (3-5 days/week)',
      very: 'Very active (6-7 days/week)',
      extremely: 'Extremely active (twice daily)',
    },
    weeklyGoal: "I'd like to lose:",
    weeklyGoalUnit: 'kg/week',
    weeklyGoalRecommendation: 'Recommended weekly weight loss: 0.5 kg/week',
  },

  dietary: {
    title: 'Dietary Preferences',
    subtitle: 'Customize your nutrition to your needs',
    dietType: 'Diet Type',
    dietOptions: {
      balanced: 'Balanced',
      vegetarian: 'Vegetarian',
      vegan: 'Vegan',
      keto: 'Keto',
      paleo: 'Paleo',
      halal: 'Halal',
    },
    allergies: 'Allergies & Intolerances',
    allergiesPlaceholder: 'e.g. Lactose, Gluten, Nuts',
    allergiesHint: 'Separate multiple allergies with commas',
    commonAllergies: {
      lactose: 'Lactose',
      gluten: 'Gluten',
      nuts: 'Nuts',
      soy: 'Soy',
      eggs: 'Eggs',
      fish: 'Fish',
      shellfish: 'Shellfish',
    },
    dislikes: 'Disliked Foods',
    dislikesPlaceholder: 'e.g. Brussels sprouts, Olives, Mushrooms',
    dislikesHint: "Foods you don't like",
    mealsPerDay: 'Meals per Day',
    mealsOptions: {
      2: '2 Meals',
      3: '3 Meals',
      4: '4 Meals',
      5: '5 Meals',
    },
  },

  security: {
    title: 'Security & Privacy',
    subtitle: 'Protect your account',

    password: {
      title: 'Change Password',
      current: 'Current Password',
      new: 'New Password',
      confirm: 'Confirm New Password',
      requirements: 'At least 8 characters, 1 uppercase, 1 number',
      changeButton: 'Change Password',
      cancelButton: 'Cancel',
    },

    twoFactor: {
      title: 'Two-Factor Authentication',
      description: 'Increase your account security',
      enabled: 'Enabled',
      disabled: 'Disabled',
      enableButton: 'Enable',
      disableButton: 'Disable',
      disableConfirm: 'Do you really want to disable 2FA?',
      qrTitle: 'Scan this QR Code',
      qrDescription: 'Use an authenticator app like Google Authenticator or Authy',
      verifyCode: 'Enter verification code',
      verifyButton: 'Verify',
    },

    devices: {
      title: 'Connected Devices',
      description: 'Manage devices with access to your account',
      current: 'Current Device',
      lastLogin: 'Last Login',
      revokeButton: 'Revoke Access',
      revokeConfirm: 'Do you really want to revoke access for this device?',
    },
  },

  subscription: {
    title: 'Subscription & Payments',
    subtitle: 'Manage your subscription',

    noplan: {
      title: 'No active subscription',
      subtitle: 'Subscribe to a plan to unlock your personalized nutrition experience.',
      cta: 'View plans',
    },

    plan: {
      current: 'Current Plan',
      basic: 'BASIC',
      vip: 'VIP',
      status: 'Status',
      statusActive: 'Active',
      statusCanceling: 'Canceling',
      statusTrialing: 'Trial',
      statusPastDue: 'Payment failed',
      statusCancelled: 'Cancelled',
      price: 'Price',
      nextBilling: 'Next Billing',
      validUntil: 'Valid Until',
      upgradeButton: 'Upgrade to VIP',
      downgradeButton: 'Change plan',
      cancelButton: 'Cancel Subscription',
      cancelingNotice: 'Your subscription will end on {{date}}. You keep full access until then.',
    },

    pastDue: {
      title: 'Payment failed',
      hint: 'Please update your payment method to keep your subscription active.',
    },

    payment: {
      title: 'Payment Method',
      type: 'Type',
      cardNumber: 'Card Number',
      expiry: 'Expiry',
      cvv: 'CVV',
      changeButton: 'Change Payment Method',
      saveButton: 'Save',
      cardPlaceholder: '1234 5678 9012 3456',
      expiryPlaceholder: 'MM/YY',
      cvvPlaceholder: '123',
      portalHint: 'Update your payment method securely via the Stripe customer portal.',
    },

    invoices: {
      title: 'Invoices',
      date: 'Date',
      amount: 'Amount',
      status: 'Status',
      download: 'Download',
      statusPaid: 'Paid',
      statusPending: 'Pending',
      statusFailed: 'Failed',
      viewAll: 'View all invoices',
      portalHint: 'Download your invoices and view your billing history via the Stripe customer portal.',
    },

    cancelModal: {
      title: 'Cancel Subscription?',
      description: 'Are you sure you want to cancel your subscription?',
      warning: 'Your subscription remains active until {{date}}. You will keep full access until then.',
      reasonLabel: 'Reason for cancellation (optional)',
      reasonPlaceholder: 'What can we do better?',
      confirmButton: 'Cancel Subscription',
      cancelButton: 'Go Back',
    },
  },

  buttons: {
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    confirm: 'Confirm',
    understand: 'Got it',
  },

  info: {
    vipNote: 'Changes to your nutrition preferences will automatically be considered in your next plan. You can generate a new plan at any time.',
    basicNote: 'With the Basic plan you can edit your data, but changes to the nutrition plan are only possible after 30 days. Upgrade to VIP for unlimited adjustments!',
    unsavedChanges: 'You have unsaved changes. Do you really want to discard them?',
  },

  success: {
    profileUpdated: 'Profile updated successfully!',
    passwordChanged: 'Password changed successfully!',
    twoFactorEnabled: '2FA enabled successfully!',
    twoFactorDisabled: '2FA disabled',
    deviceRevoked: 'Device access revoked',
    subscriptionCancelled: 'Subscription cancelled. Access until {{date}}',
    paymentUpdated: 'Payment method updated',
  },

  error: {
    loadProfile: 'Failed to load profile',
    saveProfile: 'Failed to save profile',
    passwordChange: 'Failed to change password',
    twoFactorToggle: 'Failed to toggle 2FA',
    deviceRevoke: 'Failed to revoke device access',
    subscriptionCancel: 'Failed to cancel subscription',
    paymentUpdate: 'Failed to update payment method',
    fillAllFields: 'Please fill in all fields',
    passwordMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
  },

  chatbot: {
    title: 'NutriBot Coach',
    subtitle: 'Your profile changes have been saved',
    updateMessage: '🎉 **Profile successfully updated!**\n\n📝 **The following changes were saved:**\n\n{changes}\n\n⚠️ **IMPORTANT: Would you like to automatically adjust your data?**\n\nBased on your new profile data, we can automatically adjust:\n\n📋 **Nutrition Plan** - New calorie targets and macros\n👨‍🍳 **Recipe List** - Adapted to your nutrition preferences\n🛒 **Shopping List** - Updated ingredients\n\n💡 **Reply with "Yes" or "No"**\n\n• **Yes** = All data will be automatically adjusted\n• **No** = Only profile will be updated, plan stays unchanged',
    noChanges: '✅ **No changes made.**\n\nYour data is already up to date.',
    accessNote: '💡 NutriBot has full access to your profile data',
  },
};